import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import { includes } from 'ramda';
import { isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';

import { ComponentLoader } from 'components';
import CollapsibleTable from 'components/atoms/GefMui/CollapsibleTable';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import typeTasks from 'features/other/typeTasks';
import { fetchStorePathForTaskTypeId } from 'features/other/typeTasks/utils';
import taskOverview from 'features/views/taskOverview';
import { resetTaskOverviewUserContext } from 'features/views/taskOverview/taskOverviewUserContext/actions';
import { getCurrentTaskOverviewContext } from 'features/views/taskOverview/taskOverviewUserContext/selectors';
import { getContextUser } from 'features/views/userContext/selectors';
import { getCodelistByName } from 'utils/codelists';

import TaskTableBody from './TaskTableBody';
import TaskTableHeader from './TaskTableHeader';

const { QUICK_FILTER_STATES } = taskOverview.constants;

export const TaskTable = ({ taskType, visible }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const mainFilterVisible = useSelector((state) => getVisibleStateById(state, subheaders.tasksOverview));

	const fetchStorePath = useSelector((state) => fetchStorePathForTaskTypeId(taskType.taskTypeId, state));
	const taskSubtypesCodelist = useSelector(getCodelistByName(codelists.taskSubtypes));
	const productsCodelist = useSelector(getCodelistByName(codelists.products));

	const tasks = useSelector(typeTasks.selectors.getTypeTasksById(taskType.taskTypeId));
	const allSelected = useSelector(typeTasks.selectors.getAllSelected(taskType.taskTypeId));
	const markSelectedAllState = useSelector(typeTasks.selectors.getMarkSelectedAllStateById(taskType.taskTypeId));
	const quickFilterState = useSelector(taskOverview.selectors.getQuickFilterState);
	const selectedTaskIds = useSelector(typeTasks.selectors.getSelectedTaskIdsFlatten);
	const lastRequest = useSelector(fetch.selectors.getLastRequest(fetchStorePath));
	const contextUser = useSelector(getContextUser);
	const taskOverviewUserContext = useSelector(getCurrentTaskOverviewContext);

	const prevFetchStorePath = useRef('');

	// didMount
	useEffect(() => {
		if (formIsInitiated && visible) {
			dispatch(typeTasks.actions.fetchTypeTasks(taskType.taskTypeId));
			prevFetchStorePath.current = fetchStorePath;

			if (contextUser !== taskOverviewUserContext) {
				dispatch(resetTaskOverviewUserContext());
			}
		} else {
			dispatch(typeTasks.actions.clearSelectTasks(taskType.taskTypeId));
			dispatch(typeTasks.actions.clearAllSelected(taskType.taskTypeId));
		}
		// eslint-disable-next-line
	}, [dispatch, visible, formIsInitiated]);

	// didUpdate
	useEffect(() => {
		if (formIsInitiated && visible) {
			if (prevFetchStorePath.current !== fetchStorePath) {
				dispatch(typeTasks.actions.fetchTypeTasks(taskType.taskTypeId));
				prevFetchStorePath.current = fetchStorePath;
			}
		}
		// eslint-disable-next-line
	}, [dispatch, fetchStorePath, visible, taskType.taskTypeId, tasks]);

	useEffect(
		() => () => {
			dispatch(typeTasks.actions.clearSelectTasks(taskType.taskTypeId));
			dispatch(typeTasks.actions.clearAllSelected(taskType.taskTypeId));
		},
		// eslint-disable-next-line
		[dispatch],
	);

	const handleOnReload = useCallback(() => {
		const { queryParams } = lastRequest;
		dispatch(typeTasks.actions.fetchTypeTasks(taskType.taskTypeId, queryParams.page));
	}, [dispatch, lastRequest, taskType.taskTypeId]);

	const handleLoadMore = useCallback(() => {
		dispatch(typeTasks.actions.loadMore(taskType.taskTypeId));
	}, [dispatch, taskType.taskTypeId]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(typeTasks.actions.paginationClick(taskType.taskTypeId, page - 1));
		},
		[dispatch, taskType.taskTypeId],
	);

	const handleSelectAll = useCallback(() => {
		if (allSelected) {
			dispatch(typeTasks.actions.selectAllTasks(taskType.taskTypeId, false));
		} else {
			dispatch(typeTasks.actions.selectAllTasks(taskType.taskTypeId, true));
		}

		dispatch(typeTasks.actions.clearSelectTasks(taskType.taskTypeId));
	}, [dispatch, allSelected, taskType.taskTypeId]);

	const isTask = taskType.informative === taskOverview.constants.TASK;

	const hasTrafficLights =
		isTask &&
		(quickFilterState === QUICK_FILTER_STATES.all ||
			quickFilterState === QUICK_FILTER_STATES.tasks ||
			mainFilterVisible);

	const isNotProcessedTask = quickFilterState !== QUICK_FILTER_STATES.processed;

	return (
		<ComponentLoader fetchStorePath={fetchStorePath} onReload={handleOnReload}>
			<Paper sx={{ borderRadius: 1.5 }}>
				<CollapsibleTable
					tableId="TaskTable"
					autoCloseOtherRows
					tableHeader={
						<TaskTableHeader
							isNotProcessedTask={isNotProcessedTask}
							isTask={isTask}
							mainFilterVisible={mainFilterVisible}
							allSelected={allSelected}
							onSelectAll={handleSelectAll}
							tasks={tasks}
							taskTypeId={taskType.taskTypeId}
						/>
					}
					tableBody={tasks.map((task) => (
						<TaskTableBody
							key={task.id}
							id={task.id}
							productsCodelist={productsCodelist}
							taskSubtypesCodelist={taskSubtypesCodelist}
							hasTrafficLights={hasTrafficLights}
							task={task}
							isTask={isTask}
							isSelected={includes(task.id, selectedTaskIds)}
							isNotProcessedTask={isNotProcessedTask}
							markSelectedAllState={markSelectedAllState}
						/>
					))}
				>
					<PaginationRow
						fetchStorePath={fetchStorePath}
						colSpan={100}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</CollapsibleTable>
			</Paper>
		</ComponentLoader>
	);
};

TaskTable.propTypes = {
	taskType: PropTypes.object,
	visible: PropTypes.bool,
};

export default TaskTable;
